import { useMutation, useQuery, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import type { Field } from "@circle-react/components/shared/ProfileFields/FormFields/CustomFieldsList";
import {
  reactQueryGet,
  reactQueryPost,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { PROFILE_INFO_QUERY_KEY } from "@circle-react/hooks/profile/useProfileInfo";
import { useToast } from "@circle-react-uikit/ToastV2";
import { getProfileFieldDetailQueryKey } from "./useProfileFieldDetail";
import { processBackendResponse as processListResponse } from "./useProfileFields";

export const PAGE_PROFILE_FIELD_KEY = "page-profile-fields";

const processBackendResponse = (data: Record<string, unknown>) => {
  const {
    visible_profile_fields: visibleFields,
    hidden_profile_fields: hiddenFields,
  } = data;
  return {
    visibleFields: processListResponse(visibleFields),
    hiddenFields: processListResponse(hiddenFields),
  };
};

export interface ReorderFieldsParams {
  hideProfileField?: Field;
  reorderedProfileFields: Field[];
  unhideProfileField?: Field;
}

/**
 * This is specific for each page
 * split into hidden and visible sections
 */
export const usePageProfileFields = ({
  currentPage,
}: {
  currentPage: string;
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const queryKey = [PAGE_PROFILE_FIELD_KEY, currentPage];
  const getOptions = useQuery(queryKey, async () => {
    const response = await reactQueryGet(
      internalApi.profileFields.pageIndex(currentPage),
    );
    if (!response) return;
    return processBackendResponse(response);
  });

  const reorderFields = useMutation<
    Record<string, unknown>,
    unknown,
    ReorderFieldsParams,
    unknown
  >(
    ({ reorderedProfileFields, hideProfileField, unhideProfileField }) =>
      reactQueryPost(internalApi.profileFields.pageFieldReorder(), {
        page_name: currentPage,
        reordered_profile_fields: reorderedProfileFields?.map(({ id }) => id),
        hide_profile_field_id: hideProfileField?.id,
        unhide_profile_field_id: unhideProfileField?.id,
      }),
    {
      onError: () => {
        toast.error(t("something_went_wrong"));
      },
      onSuccess: data => {
        queryClient.setQueryData(queryKey, processBackendResponse(data));
        void queryClient.invalidateQueries([getProfileFieldDetailQueryKey()]);
        queryClient.removeQueries(PROFILE_INFO_QUERY_KEY);
      },
    },
  );

  return {
    getOptions,
    reorderFields,
  };
};
